
type UserChannel = 'encrypted'| 'groupEncrypted';

function BHSocket() {
  let connected = false;
  let pusherConnection = null;
  let encryptedChannel = null;
  let groupEncryptedChannel = null;

  const queuedOn: Parameters<typeof on>[] = [];

  /**
   * Get server connection status
   * @returns Returns if pusher client is connected to the server
   */
  function isConnected() {
    return connected;
  }

  /**
   * Pusher connects to server
   */
  async function onConnected() {
    connected = true;

    if (page_info.user_type === 'client') {
      subClientChannel();
    } else if (page_info.user_type === 'counselor') {
      subCounselorChannel();
    } else if (page_info.manage_user) {
      await subManageUserChannel();
    }

    for (const [channel, event, handler] of queuedOn) {
      on(channel, event, handler);
    }

    queuedOn.length = 0;

    const event = new Event('pusherConnected');
    window.dispatchEvent(event);
  }

  /**
   * Pusher disconnected from server
   */
  function onDisconnected() {
    connected = false;

    if (page_info.user_type === 'client') {
      groupEncryptedChannel.unsubscribe();
    }

    encryptedChannel.unsubscribe();
  }

  /**
   * Triggered for a counselor when a client comes online
   * @param {string[]} event List of users that came online
   */
  function watchlistOnlineEventHandler(event) {}

  /**
   * Triggered for a counselor when a client goes offline
   * @param {string[]} event List of users that went offline
   */
  function watchlistOfflineEventHandler(event) {}

  /**
   * Connects user to Pusher server
   * @returns void
   */
  function connect() {
    if (isConnected()) {
      return;
    }

    try {
      pusherConnection = new Pusher(page_info.socket.api_key, {
        cluster: page_info.socket.cluster,
        userAuthentication: {
          transport: 'ajax',
          endpoint: '/api/pusher-user-authentication',
          params: { csrf_token: page_info.csrf_token },
        },
        channelAuthorization: {
          transport: 'ajax',
          endpoint: '/api/pusher-user-authorization',
          params: {
            csrf_token: page_info.csrf_token,
          },
        },
      });

      pusherConnection.connection.bind('connected', onConnected);
      pusherConnection.connection.bind('disconnected', onDisconnected);
      pusherConnection.connection.bind('error', (error) => console.log(error));

      pusherConnection.signin();
    } catch (error) {
      console.error('Failed to connect to pusher', error);
    }
  }

  /**
   *
   * @param channel Channel type
   * @param event Name of event
   * @param data JSON object containing additional data for emitted event
   * @param id ID of the user you are sending the message to - private channel only
   * @returns
   */
  function emit(channel: UserChannel, event: string, data: Record<any, any>, id: number) {
    if (!isConnected()) {
      return;
    }

    try {
      $.post('/api/pusher-trigger', {
        channel: getEmitChannel(channel, id),
        event,
        data,
        csrf_token: page_info.csrf_token,
      });
    } catch (error) {
      if (typeof window.logger !== 'undefined') {
        window.logger.error(error);
      }
    }
  }

  function on(channel: UserChannel, event: string, handler: (notificationData: unknown) => void) {
    if (!isConnected()) {
      queuedOn.push([channel, event, handler]);
      return;
    }

    getChannel(channel).bind(event, handler);
  }

  function off(channel: UserChannel, event: string, handler: (notificationData: unknown) => void) {
    getChannel(channel).unbind(event, handler);
  }

  /**
   * Get channel
   */
  function getChannel(channel: UserChannel) {
    switch (channel) {
      case 'encrypted':
        return encryptedChannel;
      case 'groupEncrypted':
        return groupEncryptedChannel;
      default:
        return encryptedChannel;
    }
  }

  /**
   * Subscribe to client channels
   */
  function subClientChannel() {
    groupEncryptedChannel = pusherConnection.subscribe(setChannelEnvironment(`private-encrypted-to_client_g-${page_info.counselor_id}`));
    groupEncryptedChannel.bind('pusher:subscription_error', (data) => {
      if (typeof window.logger !== 'undefined') {
        const { type = '', error = '', status = '' } = data;
        window.logger.warning(`Failed to sub to client encrypted group channel ${type} - ${status} - ${error}`);
      }
    });

    encryptedChannel = pusherConnection.subscribe(setChannelEnvironment(`private-encrypted-to_client_p-${page_info.user_id}`));
    encryptedChannel.bind('pusher:subscription_error', (data) => {
      if (typeof window.logger !== 'undefined') {
        const { type = '', error = '', status = '' } = data;
        window.logger.warning(`Failed to sub to client encrypted channel ${type} - ${status} - ${error}`);
      }
    });
  }

  /**
   * Subscribe to counselor channels
   */
  function subCounselorChannel() {
    pusherConnection.user.watchlist.bind('online', watchlistOnlineEventHandler);
    pusherConnection.user.watchlist.bind('offline', watchlistOfflineEventHandler);
    encryptedChannel = pusherConnection.subscribe(setChannelEnvironment(`private-encrypted-to_counselor_p-${page_info.user_id}`));
    encryptedChannel.bind('pusher:subscription_error', (data) => {
      if (typeof window.logger !== 'undefined') {
        const { type = '', error = '', status = '' } = data;
        window.logger.warning(`Failed to sub to counselor encrypted channel ${type} - ${status} - ${error}`);
      }
    });
  }

  /**
   * Subscribe to manage user channels
   */
  async function subManageUserChannel() {
    const uint8 = new TextEncoder().encode(page_info.manage_user); // encode as (utf-8) Uint8Array
    const hashBuffer = await crypto.subtle.digest("SHA-256", uint8); // hash the email
    const hashArray = Array.from(new Uint8Array(hashBuffer)); // convert buffer to byte array
    const hashHex = hashArray
      .map((b) => b.toString(16).padStart(2, "0"))
      .join(""); // convert bytes to hex string
    const shortened = hashHex.slice(0, 10);
    const emailAsDecimalHash = parseInt(shortened, 16);

    encryptedChannel = pusherConnection.subscribe(setChannelEnvironment(`private-encrypted-to_manage_user_p-${emailAsDecimalHash}`));
    encryptedChannel.bind('pusher:subscription_error', (data) => {
      if (typeof window.logger !== 'undefined') {
        const { type = '', error = '', status = '' } = data;
        window.logger.warning(`Failed to sub to manage encrypted channel ${type} - ${status} - ${error}`);
      }
    });
  }

  /**
   * Get channel name for emitting
   * @param channel Which channel type you are trying to get
   * @param id ID of specific user you want to send the message to
   */
  function getEmitChannel(channel, id) {
    let channelName = '';

    if (page_info.user_type === 'client' && page_info.counselor_id) {
      if (channel === 'encrypted') {
        channelName = `private-encrypted-to_counselor_p-${page_info.counselor_id}`;
      }
    }

    if (page_info.user_type === 'counselor') {
      if (channel === 'encrypted' && id) {
        channelName = `private-encrypted-to_client_p-${id}`;
      }
      if (channel === 'groupEncrypted') {
        channelName = `private-encrypted-to_client_g-${page_info.user_id}`;
      }
    }

    return setChannelEnvironment(channelName);
  }

  /**
   * Adds special environment suffix to channel name to prevent server overlap
   * @param channel String representation of channel to add environment information to
   * @returns New channel name with environment suffix
   */
  const setChannelEnvironment = (channel) => {
    let envChannel = channel;

    if (page_info.is_non_production_cloud) {
      envChannel += `-${page_info.subdomain}`;
    }

    if (page_info.is_development_local) {
      envChannel += `-${page_info.environment_name}`;
    }

    return envChannel;
  };

  return {
    connect,
    isConnected,
    emit,
    on,
    off,
  };
}

window.bh_socket = BHSocket();

// Dyanmically add script to load async and prevent race condition
const $pusher_script = $('#pusher-script');
$pusher_script.on('load', () => {
  bh_socket.connect();
});
$pusher_script.attr('src', page_info.pusher_script);
